const SKIPPED_VCF_PROPERTIES = new Set([
  'BEGIN',
  'END',
  'VERSION',
  'PRODID',
  'PHOTO',
  'NOTE',
  'ORG',
  'ADR',
  'URL',
  'NICKNAME',
  'TITLE',
  'REV',
  'UID',
  'CATEGORIES',
  'ANNIVERSARY',
  'RELATED',
  'IMPP',
  'X-ABUID',
  'X-ABLABEL',
  'X-ABADR',
]);

const IGNORED_TEL_EMAIL_TYPES = new Set(['INTERNET', 'VOICE', 'PREF', 'IPHONE', 'MSG']);

const unfoldSkippingPhoto = (text) => {
  const rawLines = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const lines = [];
  let skippingPhoto = false;

  rawLines.forEach((line) => {
    const isFolded = line.startsWith(' ') || line.startsWith('\t');
    if (skippingPhoto) {
      if (isFolded) {
        return;
      }
      skippingPhoto = false;
    }

    const content = line.trimStart();
    if (/^PHOTO[;:]/i.test(content)) {
      skippingPhoto = true;
      return;
    }

    if (isFolded && lines.length > 0) {
      lines[lines.length - 1] += line.slice(1);
      return;
    }

    if (line.trim()) {
      lines.push(line);
    }
  });

  return lines;
};

const unescapeVCardValue = (value) => String(value || '')
  .replace(/\\n/gi, '\n')
  .replace(/\\,/g, ',')
  .replace(/\\;/g, ';')
  .replace(/\\\\/g, '\\')
  .trim();

const parseTypes = (params) => params
  .flatMap((param) => {
    const [key, rawValue] = param.split('=');
    if (!rawValue) {
      return [key];
    }
    if (!/^type$/i.test(key.trim())) {
      return [];
    }
    return rawValue.split(',').map((part) => part.trim());
  })
  .filter(Boolean)
  .map((type) => type.replace(/^["']|["']$/g, '').toUpperCase());

const primaryType = (types) => {
  if (types.includes('CELL') || types.includes('MOBILE')) {
    return 'CELL';
  }
  if (types.includes('WORK')) {
    return 'WORK';
  }
  if (types.includes('HOME')) {
    return 'HOME';
  }
  const meaningful = types.find((type) => !IGNORED_TEL_EMAIL_TYPES.has(type));
  return meaningful || null;
};

const labelForProperty = (name, types) => {
  if (name === 'FN') return 'FN';
  if (name === 'BDAY') return 'BDAY';
  if (name === 'GENDER' || name === 'SEX') return 'GENDER';
  if (name === 'EMAIL' || name === 'TEL') {
    const type = primaryType(types);
    return type ? `${name} (${type})` : name;
  }
  return name;
};

const uniqueLabel = (used, label) => {
  if (!used.has(label)) {
    used.add(label);
    return label;
  }

  let index = 2;
  let next = `${label} ${index}`;
  while (used.has(next)) {
    index += 1;
    next = `${label} ${index}`;
  }
  used.add(next);
  return next;
};

const parseVCardProperty = (line) => {
  const colonIndex = line.indexOf(':');
  if (colonIndex < 0) {
    return null;
  }

  const spec = line.slice(0, colonIndex);
  const value = unescapeVCardValue(line.slice(colonIndex + 1));
  const [rawName, ...params] = spec.split(';');
  const name = (rawName || '').split('.').pop().toUpperCase();
  if (!name || SKIPPED_VCF_PROPERTIES.has(name) || name.startsWith('X-')) {
    return null;
  }

  return {
    name,
    types: parseTypes(params),
    value,
  };
};

const cardToValues = (cardLines) => {
  const values = {};
  const used = new Set();

  cardLines.forEach((line) => {
    const property = parseVCardProperty(line);
    if (!property) {
      return;
    }

    if (property.name === 'N') {
      const [lastName, firstName] = property.value.split(';');
      if (firstName?.trim()) {
        values[uniqueLabel(used, 'N (First)')] = firstName.trim();
      }
      if (lastName?.trim()) {
        values[uniqueLabel(used, 'N (Last)')] = lastName.trim();
      }
      return;
    }

    if (!property.value) {
      return;
    }

    const label = uniqueLabel(used, labelForProperty(property.name, property.types));
    values[label] = property.value;
  });

  return values;
};

export const parseVcfCustomerFile = (fileText) => {
  const lines = unfoldSkippingPhoto(fileText);
  const cards = [];
  let current = null;

  lines.forEach((line) => {
    if (/^BEGIN:VCARD$/i.test(line.trim())) {
      current = [];
      return;
    }
    if (/^END:VCARD$/i.test(line.trim())) {
      if (current) {
        cards.push(current);
      }
      current = null;
      return;
    }
    if (current) {
      current.push(line);
    }
  });

  const records = cards
    .map((cardLines) => cardToValues(cardLines))
    .filter((values) => Object.keys(values).length > 0);

  const sourceFields = [];
  const seen = new Set();
  records.forEach((values) => {
    Object.keys(values).forEach((field) => {
      if (seen.has(field)) return;
      seen.add(field);
      sourceFields.push(field);
    });
  });

  if (!records.length) {
    return { records: [], sourceFields: [], errorKey: 'customersManagement.import.errors.emptyFile' };
  }

  return { records, sourceFields, errorKey: null };
};
